import { z } from "zod";
import { orderCardSchema, orderSummarySchema } from "./order-card";
import { replyStatesSchema, transferReasonSchema } from "./reply";
import { pendingEmailCodeSchema } from "./customer-email-code";
import { supportRequestBlockDataSchema } from "./support-request";

export const orderListDataSchema = z.strictObject({
  orders: z.array(orderSummarySchema).max(50),
  next_cursor: z.string().min(1).max(2048).nullable(),
  fetched_at: z.iso.datetime(),
});
export const orderDetailDataSchema =
  orderCardSchema.options[1].shape.payload.omit({ actions: true });
export const cardsAuthSchema = z.object({
  store_login: z.enum(["signed_in", "signed_out", "unknown"]),
  email_code: z.enum(["unavailable", "available", "verified"]),
  pending_email_code: pendingEmailCodeSchema.optional(),
  login_hint_email: z.string().optional(),
});
const scope = {
  uid: z.string().min(1).max(256),
  conversation_id: z.string().min(1).max(128),
  liquid_customer_id: z.string().regex(/^\d*$/).optional(),
};
export const cardsRequestSchema = z.strictObject({
  ...scope,
  messages: z
    .array(
      z.strictObject({
        message_id: z.string().min(1).max(128),
        transfer_reason: transferReasonSchema.nullish(),
        reply_states: replyStatesSchema,
      }),
    )
    .min(1)
    .max(20),
});
export const ordersPageRequestSchema = z.strictObject({
  ...scope,
  ref: z.string().regex(/^sv1_[A-Za-z0-9_-]{22}$/),
  cursor: z.string().min(1).max(2048),
});
export const blockActionSchema = z.enum([
  "store_login",
  "email_code",
  "select_order",
  "load_more",
  "not_received",
  "track_shipment",
  "view_order",
  "start_order_check",
]);
const common = {
  state_index: z.int().min(0).max(4),
  actions: z.array(blockActionSchema),
};
const types = z.enum([
  "verification",
  "order_list",
  "order_list_empty",
  "order_detail",
  "order_check",
  "support_request",
]);
export const cardBlockSchema = z.union([
  z.strictObject({
    ...common,
    type: types,
    status: z.literal("unavailable"),
    actions: z.array(blockActionSchema).length(0),
  }),
  z.strictObject({
    ...common,
    type: types,
    status: z.literal("needs_verification"),
  }),
  z.strictObject({
    ...common,
    type: z.literal("verification"),
    status: z.literal("ok"),
    data: z.strictObject({
      auth_request_id: z.string(),
      status: z.enum(["pending", "verified", "expired", "cancelled"]),
      expires_at: z.iso.datetime(),
    }),
  }),
  z.strictObject({
    ...common,
    type: z.literal("order_list"),
    status: z.literal("ok"),
    data: orderListDataSchema,
  }),
  z.strictObject({
    ...common,
    type: z.literal("order_list_empty"),
    status: z.literal("ok"),
    data: z.strictObject({ fetched_at: z.iso.datetime() }),
  }),
  z.strictObject({
    ...common,
    type: z.literal("order_detail"),
    status: z.literal("ok"),
    data: orderDetailDataSchema,
  }),
  z.strictObject({
    ...common,
    type: z.literal("order_check"),
    status: z.literal("ok"),
  }),
  z.strictObject({
    ...common,
    type: z.literal("support_request"),
    status: z.literal("ok"),
    data: supportRequestBlockDataSchema,
  }),
]);
export type CardBlock = z.infer<typeof cardBlockSchema>;
export type CardsRequest = z.infer<typeof cardsRequestSchema>;
export const cardsResponseSchema = z.strictObject({
  auth: cardsAuthSchema,
  messages: z.array(
    z.strictObject({
      message_id: z.string(),
      blocks: z.array(cardBlockSchema),
    }),
  ),
  retry_after: z.int().positive().optional(),
});
export type CardsResponse = z.infer<typeof cardsResponseSchema>;
export const ordersPageResponseSchema = z.union([
  z.strictObject({ status: z.literal("ok"), data: orderListDataSchema }),
  z.strictObject({
    status: z.enum(["needs_verification", "unavailable"]),
    retry_after: z.int().positive().optional(),
  }),
]);
export type OrdersPageResponse = z.infer<typeof ordersPageResponseSchema>;
